import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtEuro, ROLE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useNavigate } from "react-router-dom";
import { Wallet } from "lucide-react";
import { toast } from "sonner";

const STATO_LABEL = { in_lavorazione: "In lavorazione", pagato: "Pagato", annullato: "Annullato" };

export default function Amministrazione() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ entrata: 0, uscita: 0, importo: 0 });
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [soggetti, setSoggetti] = useState([]);
  const [fStato, setFStato] = useState("in_lavorazione");
  const [fSogg, setFSogg] = useState("all");
  const [fTipo, setFTipo] = useState("all");
  const [fProd, setFProd] = useState("all");
  const [prodotti, setProdotti] = useState([]);
  const [fLiqDal, setFLiqDal] = useState("");
  const [fLiqAl, setFLiqAl] = useState("");
  const [sel, setSel] = useState([]);
  const [bulk, setBulk] = useState("pagato");

  const load = useCallback(() => {
    const params = { page, page_size: pageSize };
    if (fStato !== "all") params.stato = fStato;
    if (fSogg !== "all") params.soggetto = fSogg;
    if (fTipo !== "all") params.tipo = fTipo;
    if (fProd !== "all") params.prodotto = fProd;
    if (fLiqDal) params.liq_dal = fLiqDal;
    if (fLiqAl) params.liq_al = fLiqAl;
    api.get("/admin/provvigioni", { params }).then((r) => {
      setRows(r.data.items || []); setTotal(r.data.total || 0);
      setTotals(r.data.totals || { entrata: 0, uscita: 0, importo: 0 }); setSel([]);
    });
  }, [fStato, fSogg, fTipo, fProd, fLiqDal, fLiqAl, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [fStato, fSogg, fTipo, fProd, fLiqDal, fLiqAl]);
  useEffect(() => { api.get("/products").then((r) => setProdotti(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) api.get("/admin/provvigioni/soggetti").then((r) => setSoggetti(r.data)).catch(() => {}); }, [isAdmin]);

  const totIn = totals.entrata;
  const totOut = totals.uscita;
  const totImporto = totals.importo;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const changeStato = async (x, stato) => {
    try { await api.put(`/practices/${x.practice_id}/provvigioni/${x.id}`, { stato }); toast.success("Stato aggiornato · email inviata al soggetto"); load(); }
    catch { toast.error("Errore"); }
  };
  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const applyBulk = async () => {
    if (sel.length === 0) { toast.error("Seleziona almeno una provvigione"); return; }
    const items = rows.filter((x) => sel.includes(x.id)).map((x) => ({ practice_id: x.practice_id, prov_id: x.id }));
    try { const { data } = await api.post("/admin/provvigioni/bulk-stato", { stato: bulk, items }); toast.success(`${data.updated} provvigioni aggiornate`); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="amministrazione-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Wallet className="h-6 w-6 text-primary" /> {isAdmin ? "Amministrazione Provvigioni" : "Le mie provvigioni"}</h1>
        <p className="text-sm text-muted-foreground mt-1">{total} provvigioni · Entrate {fmtEuro(totIn)} · Uscite {fmtEuro(totOut)} · Netto {fmtEuro(totIn - totOut)}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={fTipo} onValueChange={setFTipo}>
          <SelectTrigger className="w-40" data-testid="admin-filter-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Entrate e uscite</SelectItem>
            <SelectItem value="entrata">Solo entrate</SelectItem>
            <SelectItem value="uscita">Solo uscite</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fStato} onValueChange={setFStato}>
          <SelectTrigger className="w-44" data-testid="admin-filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
            <SelectItem value="pagato">Pagato</SelectItem>
            <SelectItem value="annullato">Annullato</SelectItem>
          </SelectContent>
        </Select>
        {prodotti.length > 0 && (
          <SearchableSelect value={fProd} onValueChange={setFProd} testid="admin-filter-prodotto" className="w-52" searchPlaceholder="Cerca prodotto…"
            options={[{ value: "all", label: "Tutti i prodotti" }, ...prodotti.map((v) => ({ value: v, label: v }))]} />
        )}
        {isAdmin && (
          <SearchableSelect value={fSogg} onValueChange={setFSogg} testid="admin-filter-soggetto" className="w-52" searchPlaceholder="Cerca soggetto…"
            options={[{ value: "all", label: "Tutti i soggetti" }, ...soggetti.map((s) => ({ value: s.id, label: s.nome }))]} />
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">Liquidazione dal</Label>
          <Input type="date" className="w-[8.5rem]" value={fLiqDal} onChange={(e) => setFLiqDal(e.target.value)} data-testid="admin-filter-liq-dal" />
          <Label className="text-xs text-muted-foreground whitespace-nowrap">al</Label>
          <Input type="date" className="w-[8.5rem]" value={fLiqAl} onChange={(e) => setFLiqAl(e.target.value)} data-testid="admin-filter-liq-al" />
          {(fLiqDal || fLiqAl) && <Button variant="ghost" size="sm" data-testid="admin-filter-liq-clear" onClick={() => { setFLiqDal(""); setFLiqAl(""); }}>Azzera</Button>}
        </div>
        {sel.length > 0 && (
          <div className="flex items-center gap-2 ml-auto" data-testid="bulk-bar">
            <span className="text-sm text-muted-foreground">{sel.length} selezionate →</span>
            <Select value={bulk} onValueChange={setBulk}>
              <SelectTrigger className="w-40" data-testid="bulk-stato-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                <SelectItem value="pagato">Pagato</SelectItem>
                <SelectItem value="annullato">Annullato</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="bulk-apply-btn" onClick={applyBulk}>Applica</Button>
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-prov-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-3">{isAdmin && <Checkbox checked={sel.length === rows.length && rows.length > 0} onCheckedChange={(v) => setSel(v ? rows.map((x) => x.id) : [])} data-testid="select-all" />}</th>
                <th className="px-4 py-3 font-semibold">Pratica</th>
                <th className="px-4 py-3 font-semibold">Cliente</th>
                <th className="px-4 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">Soggetto</th>
                <th className="px-4 py-3 font-semibold">Importo</th>
                <th className="px-4 py-3 font-semibold">Stato</th>
                <th className="px-4 py-3 font-semibold">Liquidazione</th>
                <th className="px-4 py-3 font-semibold">Data</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id} className="border-b border-border last:border-0 hover:bg-accent/30" data-testid={`admin-prov-row-${x.id}`}>
                  <td className="px-3 py-3">{isAdmin && <Checkbox checked={sel.includes(x.id)} onCheckedChange={() => toggleSel(x.id)} data-testid={`sel-${x.id}`} />}</td>
                  <td className="px-4 py-3"><button className="text-primary font-mono text-xs hover:underline" data-testid={`open-practice-${x.id}`} onClick={() => navigate(`/pratiche/${x.practice_id}`)}>{x.practice_numero}</button></td>
                  <td className="px-4 py-3" data-testid={`admin-cliente-${x.id}`}>{x.cliente_nome || "—"}</td>
                  <td className="px-4 py-3">{x.tipo === "entrata" ? "Entrata" : "Uscita"}</td>
                  <td className="px-4 py-3">{x.soggetto_nome || "—"}{x.soggetto_ruolo && <span className="text-xs text-muted-foreground"> · {ROLE_LABELS[x.soggetto_ruolo] || x.soggetto_ruolo}</span>}</td>
                  <td className={`px-4 py-3 font-semibold ${x.stato === "annullato" ? "line-through text-muted-foreground" : ""}`}>{fmtEuro(x.importo)}</td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                    <Select value={x.stato || "in_lavorazione"} onValueChange={(v) => changeStato(x, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid={`admin-stato-${x.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                        <SelectItem value="pagato">Pagato</SelectItem>
                        <SelectItem value="annullato">Annullato</SelectItem>
                      </SelectContent>
                    </Select>
                    ) : (
                    <span className="text-xs px-2 py-1 rounded bg-secondary" data-testid={`admin-stato-${x.id}`}>{STATO_LABEL[x.stato || "in_lavorazione"]}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs" data-testid={`admin-liq-${x.id}`}>{x.liquidated_at || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{x.data}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Nessuna provvigione</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="border-t-2 border-border bg-muted/40">
                <tr className="font-semibold" data-testid="admin-prov-footer">
                  <td className="px-4 py-3 text-right text-xs uppercase tracking-wider text-muted-foreground" colSpan={5}>Totale importo</td>
                  <td className="px-4 py-3" data-testid="admin-total-importo">{fmtEuro(totImporto)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="md:hidden divide-y divide-border" data-testid="admin-prov-mobile-list">
          {rows.map((x) => (
            <div key={x.id} className="p-4" data-testid={`admin-prov-card-${x.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {isAdmin && <Checkbox checked={sel.includes(x.id)} onCheckedChange={() => toggleSel(x.id)} data-testid={`sel-m-${x.id}`} />}
                  <button className="text-primary font-mono text-xs hover:underline" data-testid={`open-practice-m-${x.id}`} onClick={() => navigate(`/pratiche/${x.practice_id}`)}>{x.practice_numero}</button>
                </div>
                <span className={`font-semibold ${x.stato === "annullato" ? "line-through text-muted-foreground" : ""}`}>{fmtEuro(x.importo)}</span>
              </div>
              <div className="mt-1.5 grid grid-cols-1 gap-y-1 text-xs text-muted-foreground">
                <div><span className="font-medium text-foreground">Cliente:</span> {x.cliente_nome || "—"}</div>
                <div><span className="font-medium text-foreground">Tipo:</span> {x.tipo === "entrata" ? "Entrata" : "Uscita"}</div>
                <div><span className="font-medium text-foreground">Soggetto:</span> {x.soggetto_nome || "—"}{x.soggetto_ruolo && ` · ${ROLE_LABELS[x.soggetto_ruolo] || x.soggetto_ruolo}`}</div>
                {x.liquidated_at && <div><span className="font-medium text-foreground">Liquidazione:</span> {x.liquidated_at}</div>}
                <div><span className="font-medium text-foreground">Data:</span> {x.data}</div>
              </div>
              <div className="mt-2">
                {isAdmin ? (
                  <Select value={x.stato || "in_lavorazione"} onValueChange={(v) => changeStato(x, v)}>
                    <SelectTrigger className="h-8 w-40 text-xs" data-testid={`admin-stato-m-${x.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                      <SelectItem value="pagato">Pagato</SelectItem>
                      <SelectItem value="annullato">Annullato</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs px-2 py-1 rounded bg-secondary" data-testid={`admin-stato-m-${x.id}`}>{STATO_LABEL[x.stato || "in_lavorazione"]}</span>
                )}
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="px-4 py-10 text-center text-muted-foreground">Nessuna provvigione</div>}
          {rows.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 font-semibold bg-muted/40" data-testid="admin-prov-footer-m">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Totale importo</span>
              <span data-testid="admin-total-importo-m">{fmtEuro(totImporto)}</span>
            </div>
          )}
        </div>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-between" data-testid="admin-prov-pagination">
          <span className="text-sm text-muted-foreground">Pagina {page} di {pageCount} · {total} provvigioni</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="admin-prev-page" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedente</Button>
            <Button variant="outline" size="sm" data-testid="admin-next-page" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Successiva</Button>
          </div>
        </div>
      )}
    </div>
  );
}
